import React, { useCallback, useEffect, useState } from "react";

import { makeGet } from "@culturehq/client";
import { ModalDialog } from "@culturehq/components";
import { SelectOption, SelectValueWithCategory } from "@culturehq/components/dist/components/form/typings";

import { ApprovedForSocial, ExternalMapper, ExternalMapperValue, ExternalMapperValueAssociation, Story, StoryQuestion } from "chq";
import { useProfile } from "app/context";
import { onFetchAll } from "app/hooks/usePagedEntity";
import { useDoor, useIsMounted } from "app/hooks";
import { t, Trans } from "app/utils";

import { StoryWithNullableQuestion } from "components/stories/post-stories-v2/types";
import ChqModalDialog from "components/common/ChqModalDialog";
import JobMappingForm from "./JobMappingForm";

interface JobPostingMappingModalProps {
  approvedForSocial?: ApprovedForSocial;
  children: any;
  editImmediately?: boolean;
  mappersValueIds?: string[];
  story?: Story | StoryWithNullableQuestion;
  storyQuestion?: StoryQuestion;
  onUpdate: (value: ApprovedForSocial | Story | Record<string, string[]>) => void;
}

interface JobPostingMappingTextProps {
  story?: Story | StoryWithNullableQuestion;
  storyQuestion?: StoryQuestion;
}

export type MapField = ExternalMapper & {
  options: SelectOption[];
}

export type DataItem = number | number[];

const JobPostingMappingText: React.FC<JobPostingMappingTextProps> = ({ story, storyQuestion }) => {
  if (story) {
    return (
      <Trans>
        Use the fields below to map this story to one or more jobs.
        The topmost field with a tag will be the active field mapped
        once it has been approved.
      </Trans>
    );
  }

  return (
    <Trans>
      Use the fields below to map all the new stories in this prompt to one or more jobs.
      The topmost field with a tag will be the active field mapped once the story has
      been approved.
    </Trans>
  );
};

const onFetchExternalMapperPage = (externalMapperId: number) => (
  (page: number) => makeGet("/external_mapper_values", { page, externalMapperId })
);

const JobPostingMappingModal: React.FC<JobPostingMappingModalProps> = ({
  approvedForSocial, children, editImmediately, mappersValueIds, onUpdate, story, storyQuestion
}) => {
  const { profile: { organization } } = useProfile();
  const isMounted = useIsMounted();
  const [changed, setChanged] = useState(false);
  const { open, onOpen, onClose: onCloseModal } = useDoor(false);
  const [externalMappers, setExternalMappers] = useState<MapField[]>([]);
  const [selectedValues, setSelectedValues] = useState<{ [key: string]: DataItem }>({});
  const [loading, setLoading] = useState(true);

  const externalMapperValueAssociations = story?.externalMapperValueAssociations
    || approvedForSocial?.entity?.externalMapperValueAssociations
    || storyQuestion?.parentStoryQuestion?.externalMapperValueAssociations
    || storyQuestion?.externalMapperValueAssociations;

  const transformData = useCallback((
    data: ExternalMapperValueAssociation[]
  ): { [key: string]: DataItem } => (
    data.reduce((acc, item) => {
      const { id, allowMultiple } = item.externalMapper;
      // Initialize the group if it does not exist
      if (!acc[id]) {
        acc[id] = allowMultiple ? [] : 0;
      }

      // Add to selectedMappersIds
      if (allowMultiple) {
        (acc[id] as number[]).push(item.externalMapperValue.id);
      } else {
        acc[id] = item.externalMapperValue.id;
      }

      return acc;
    }, {} as { [key: number]: DataItem })
  ), []);

  useEffect(
    () => {
      if (externalMapperValueAssociations) {
        setSelectedValues(transformData(externalMapperValueAssociations));
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transformData]
  );

  useEffect(
    () => {
      if (
        open && !editImmediately && !loading && mappersValueIds && mappersValueIds.length > 0
        // && !(externalMapperValueAssociations && externalMapperValueAssociations.length > 0)
      ) {
        const result = mappersValueIds.reduce((acc, id) => {
          externalMappers.forEach(mapper => {
            // Check if the current ID exists in the mapper's options
            const optionExists = mapper.options.some(
              option => option.value.toString() === id || option.value === id
            );

            if (optionExists) {
              if (mapper.allowMultiple) {
                // Initialize the key if it doesn't exist
                if (!acc[mapper.id]) {
                  acc[mapper.id] = [];
                }
                // Add the matched ID to the mapper's array
                (acc[mapper.id] as number[]).push(Number(id)); // Convert to number if needed
              } else {
                acc[mapper.id] = Number(id);
              }
            }
          });

          return acc;
        }, {} as Record<number, number[] | number>);
        setSelectedValues(result);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editImmediately, loading, open]
  );

  useEffect(() => {
    if (open && externalMappers.length > 0) {
      const fetchExternalMapperValues = (mapField: MapField) => (
        onFetchAll("externalMapperValues", onFetchExternalMapperPage(mapField.id)).then(({ externalMapperValues }: any) => {
          if (isMounted) {
            setExternalMappers(prevExternalMappers => ([...prevExternalMappers.map(
              (externalMapper: MapField) => {
                if (externalMapper.id !== mapField.id) return externalMapper;
                return {
                  ...externalMapper,
                  options: externalMapperValues.map((externalMapperValue: ExternalMapperValue) => ({
                    value: externalMapperValue.id,
                    label: externalMapperValue.value
                  }))
                };
              }
            )]));
          }
        })
      );

      const fetchPromises = externalMappers.map(fetchExternalMapperValues);
      Promise.all(fetchPromises).then(() => {
        if (isMounted) setLoading(false);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalMappers.length, open]);

  useEffect(() => {
    if (isMounted && organization.externalMappers) {
      setExternalMappers(organization.externalMappers.map(
        (externalMapper: ExternalMapper) => ({
          ...externalMapper,
          options: []
        })
      ));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const restartState = useCallback(() => {
    setChanged(false);
    setSelectedValues(
      externalMapperValueAssociations ? transformData(externalMapperValueAssociations) : {}
    );
    onCloseModal();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConfirmClose = useCallback(
    () => {
      const message = t("You have unsaved changes, are you sure you'd like to close the job posting mapping modal?");

      // eslint-disable-next-line no-alert
      if (!changed || window.confirm(message)) restartState();
    },
    [changed, restartState]
  );

  const handleSubmit = () => {
    restartState();
  };

  const handleSelectedValue = (field: MapField, value: string | SelectValueWithCategory | null) => {
    setSelectedValues(prevSelectedValues => ({
      ...prevSelectedValues,
      [field.id]: value
    }));
    setChanged(true);
  };

  return (
    <>
      {children(onOpen)}
      {open && (
        <ChqModalDialog onClose={onConfirmClose} className="job-posting-modal">
          <ModalDialog.Heading onClose={onConfirmClose}>
            <Trans>Job Posting Mapping</Trans>
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <div className="job-posting-modal_info">
              <JobPostingMappingText story={story} storyQuestion={storyQuestion} />
            </div>
            <JobMappingForm
              approvedForSocial={approvedForSocial}
              editImmediately={editImmediately}
              externalMappers={externalMappers}
              loading={loading}
              onSelectedValue={handleSelectedValue}
              onSubmit={handleSubmit}
              onUpdate={onUpdate}
              open={open}
              selectedValues={selectedValues}
              story={story}
            />
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default JobPostingMappingModal;
